import React, { useState } from 'react';
import {
  FileSpreadsheet,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Smartphone,
  Shield,
  Users,
  Zap,
  RefreshCw,
  QrCode,
  DollarSign,
  Layers,
  Sparkles
} from 'lucide-react';
import { ERP_WORKFLOWS, ErpSolution } from '../data/siteData';
import { CustomImage } from './CustomImage';

interface CustomErpSectionProps {
  onOpenBooking: (serviceName?: string) => void;
}

export const CustomErpSection: React.FC<CustomErpSectionProps> = ({ onOpenBooking }) => {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('inventory');
  const [activeRole, setActiveRole] = useState<'staff' | 'manager'>('staff');
  const [scannedFeedback, setScannedFeedback] = useState<string | null>(null);

  const currentWorkflow = ERP_WORKFLOWS.find((w) => w.id === selectedWorkflowId) || ERP_WORKFLOWS[0];

  const handleSimulateScan = () => {
    setScannedFeedback('Simulated barcode scan: SKU CAT6-500M (+1 verified in Nairobi Warehouse)');
    setTimeout(() => setScannedFeedback(null), 3000);
  };

  return (
    <section id="custom-erps" className="py-20 lg:py-28 bg-[#080d11] text-white border-b border-slate-800 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/3 left-0 w-96 h-96 bg-[#b88e4c]/10 blur-[130px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/10 blur-[130px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Balanced Section Header: 2-Column High-Converting Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-16 text-left">
          
          {/* Left Column: Copy & Commercial Hook */}
          <div className="lg:col-span-7 space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#009fe3]/30 bg-slate-900/40 px-3.5 py-1 text-xs font-semibold text-[#009fe3]">
              <FileSpreadsheet className="h-3.5 w-3.5 text-[#009fe3]" />
              <span>Custom ERPs &amp; Internal Portals // Nairobi, Kenya</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-serif-heading text-balance">
              Convert your work Excels <br className="hidden sm:block" />
              to an easy-to-use app.
            </h2>

            <p className="text-sm sm:text-base text-slate-300 leading-relaxed font-sans">
              Stop running your business on 50 fragile spreadsheets with broken formulas and zero access control. We convert chaotic Excel sheets into secure, high-speed multi-user web systems with <strong>automated M-Pesa STK push</strong>, <strong>KRA eTIMS compliance</strong>, and role permissions. Systems starting from <strong>KES 45,000</strong> with <strong>Lipa Pole Pole</strong> installment options.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-3 text-xs text-slate-300 font-medium">
              <span className="flex items-center gap-1.5 bg-slate-900 border border-slate-700/80 px-3 py-2 rounded-xl">
                <Zap className="h-3.5 w-3.5 text-[#009fe3]" />
                <span>Shipped in 2 to 4 weeks</span>
              </span>
              <span className="flex items-center gap-1.5 bg-slate-900 border border-slate-700/80 px-3 py-2 rounded-xl">
                <Shield className="h-3.5 w-3.5 text-[#009fe3]" />
                <span>Role-Based Access Control</span>
              </span>
              <span className="flex items-center gap-1.5 bg-slate-900 border border-slate-700/80 px-3 py-2 rounded-xl">
                <Smartphone className="h-3.5 w-3.5 text-[#009fe3]" />
                <span>Offline-ready Mobile UI</span>
              </span>
            </div>

            <div className="pt-2 flex items-center gap-4">
              <button
                onClick={() => onOpenBooking('Custom ERP & Excel Conversion')}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#009fe3] hover:bg-[#0089c4] text-white font-bold text-xs transition-all shadow-md cursor-pointer min-h-[44px]"
              >
                <span>Book Free Architecture Audit</span>
                <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-xs font-mono text-slate-400">
                Starting from KES 45,000
              </span>
            </div>
          </div>

          {/* Right Column: Visual Dashboard Mockup using CustomImage */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 relative shadow-2xl">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-950 border-b border-slate-800 text-xs font-mono">
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  <span className="text-slate-300 text-xs">erp.thinkersarcade.co.ke</span>
                </div>
                <span className="text-[#009fe3] text-xs font-bold">PostgreSQL v16</span>
              </div>
              
              <div className="relative h-56 sm:h-64 overflow-hidden">
                <CustomImage
                  src="/images/custom_erp_dashboard_1790951335673.webp"
                  alt="Custom ERP database and operations dashboard modernizing Excel spreadsheets in Kenya"
                  width={640}
                  height={280}
                  className="w-full h-full object-cover"
                  priority={false}
                  fallbackSrc="/images/custom_erp_dashboard_1790951335673.webp"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent pointer-events-none" />
                
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-xs font-mono">
                  <span className="bg-black/85 px-2.5 py-1 rounded text-[#009fe3] border border-[#009fe3]/30 text-xs">
                    Spreadsheet → Real-Time Cloud ERP
                  </span>
                  <span className="bg-emerald-950 text-emerald-400 border border-emerald-800/80 px-2 py-0.5 rounded text-xs font-bold">
                    Zero Data Loss
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Workflow Switcher Tabs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {ERP_WORKFLOWS.map((wf) => {
            const isSelected = wf.id === selectedWorkflowId;
            return (
              <button
                key={wf.id}
                onClick={() => setSelectedWorkflowId(wf.id)}
                className={`p-4 rounded-2xl text-left border transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-slate-900 border-[#b88e4c] shadow-md ring-1 ring-[#b88e4c]/30'
                    : 'bg-slate-950/80 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700'
                }`}
              >
                <div className="text-xs font-mono text-[#009fe3] font-semibold mb-1">
                  TRANSFORM //
                </div>
                <div className="text-sm font-bold text-white font-display mb-1">
                  {wf.title}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  Replaces: {wf.excelSheetName}
                </div>
              </button>
            );
          })}
        </div>

        {/* Side-by-Side: The Spreadsheet Bottleneck VS The Custom Web App */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-14">
          
          {/* Left: The Excel Problem */}
          <div className="lg:col-span-5 rounded-3xl bg-slate-950 border border-rose-900/40 p-6 sm:p-8 text-left space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-rose-950/60">
              <div className="flex items-center gap-2 text-xs font-mono text-rose-400">
                <FileSpreadsheet className="h-4 w-4" />
                <span className="truncate max-w-[200px]">{currentWorkflow.excelSheetName}</span>
              </div>
              <span className="text-xs font-bold bg-rose-950 text-rose-300 px-2 py-0.5 rounded border border-rose-800/60">
                FRAGILE SPREADSHEET
              </span>
            </div>

            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Operational Risks &amp; Bottlenecks:
            </div>

            <div className="space-y-3">
              {currentWorkflow.excelPainPoints.map((pain, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-300">
                  <AlertTriangle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
                  <span>{pain}</span>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-slate-900 text-xs text-slate-500 italic">
              "We lost 3 days of stock records last month because someone accidentally sorted columns A to G without selecting H to M."
            </div>
          </div>

          {/* Right: The High-Speed Custom Web App */}
          <div className="lg:col-span-7 rounded-3xl bg-slate-900 border border-[#009fe3]/30 p-6 sm:p-8 text-left space-y-5 shadow-2xl relative">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-800">
              <div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#009fe3] animate-pulse" />
                  <span className="text-xs font-mono text-[#009fe3] font-bold uppercase">
                    Thinkers Arcade Web App Environment
                  </span>
                </div>
                <div className="text-sm font-bold text-white font-display mt-0.5">
                  {currentWorkflow.title}
                </div>
              </div>

              {/* Role Toggle Simulation */}
              <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-lg text-xs font-medium border border-slate-800 self-start sm:self-auto">
                <span className="text-xs text-slate-400 px-1.5">View As:</span>
                <button
                  onClick={() => setActiveRole('staff')}
                  className={`px-2 py-0.5 rounded ${activeRole === 'staff' ? 'bg-[#b88e4c] text-slate-950 font-bold' : 'text-slate-400'}`}
                >
                  Staff Member
                </button>
                <button
                  onClick={() => setActiveRole('manager')}
                  className={`px-2 py-0.5 rounded ${activeRole === 'manager' ? 'bg-[#b88e4c] text-slate-950 font-bold' : 'text-slate-400'}`}
                >
                  MD / Director
                </button>
              </div>
            </div>

            {/* Interactive Live Data Table */}
            <div className="rounded-xl border border-slate-800 bg-slate-950 overflow-hidden">
              <div className="p-3 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between text-xs">
                <span className="font-mono text-slate-300">Live Database Grid (Connected)</span>
                <button
                  onClick={handleSimulateScan}
                  className="px-2.5 py-1 rounded bg-[#b88e4c]/20 hover:bg-[#009fe3]/20 text-[#009fe3] font-semibold border border-[#009fe3]/30 flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <QrCode className="h-3.5 w-3.5" />
                  <span>Simulate Barcode / STK Scan</span>
                </button>
              </div>

              {scannedFeedback && (
                <div className="bg-slate-900/80 text-[#009fe3] text-xs px-3 py-1.5 border-b border-[#009fe3]/30 animate-in fade-in">
                  ✓ {scannedFeedback}
                </div>
              )}

              <div className="overflow-x-auto p-2">
                <table className="w-full text-left text-xs font-mono">
                  <thead className="text-slate-400 border-b border-slate-800 text-xs">
                    <tr>
                      {Object.keys(currentWorkflow.sampleData[0]).map((key) => (
                        <th key={key} className="py-2 px-3 capitalize">
                          {key.replace(/([A-Z])/g, ' $1')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-900 text-slate-300">
                    {currentWorkflow.sampleData.map((row, i) => (
                      <tr key={i} className="hover:bg-slate-900/50">
                        {Object.values(row).map((val, j) => (
                          <td key={j} className="py-2 px-3">
                            {String(val).includes('Auto-Verified') || String(val).includes('In Stock') ? (
                              <span className="text-[#009fe3] bg-slate-900 px-1.5 py-0.5 rounded border border-[#009fe3]/30 text-xs">
                                {val}
                              </span>
                            ) : String(val).includes('Reorder') ? (
                              <span className="text-amber-400 bg-amber-950/60 px-1.5 py-0.5 rounded border border-amber-500/30 text-xs">
                                {val}
                              </span>
                            ) : (
                              val
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Custom App Features Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {currentWorkflow.appFeatures.map((feat, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-300">
                  <CheckCircle2 className="h-4 w-4 text-[#009fe3] shrink-0" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            {/* Conversion CTA */}
            <div className="pt-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-xs text-slate-400">Proven Outcome:</div>
                <div className="text-xl font-bold font-display text-[#009fe3]">
                  {currentWorkflow.metrics.value} {currentWorkflow.metrics.label}
                </div>
              </div>

              <button
                onClick={() => onOpenBooking(`Custom ERP: ${currentWorkflow.title}`)}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-[#b88e4c] hover:bg-[#009fe3] text-slate-950 font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition-colors shadow-md"
              >
                <span>Convert Your Sheet to an App</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

        </div>

        {/* Estimator Banner */}
        <div className="rounded-2xl bg-gradient-to-r from-slate-900 via-[#112337] to-slate-900 border border-slate-800 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-left">
          <div>
            <h3 className="text-lg font-bold text-white font-display">
              Have a specific spreadsheet you want converted this month?
            </h3>
            <p className="text-xs text-slate-300 mt-1">
              Send us your sample workbook (with dummy data). We will model the database schema and send you a working prototype demo link in 48 hours.
            </p>
          </div>
          <button
            onClick={() => onOpenBooking('Excel to Custom ERP Prototype')}
            className="rounded-xl bg-white hover:bg-slate-100 text-slate-950 font-bold px-6 py-3 text-xs whitespace-nowrap cursor-pointer transition-colors shadow-xs"
          >
            Request 48-Hour Prototype
          </button>
        </div>

      </div>
    </section>
  );
};
